/* WildEarth — Veldskoen × WildEarth "Keep Africa Close".
   One engine drives every wildearth section on the page. Each section emits this
   script; the guard below means it initialises exactly once no matter how many
   sections are present, and it re-scans the document when Shopify adds, removes,
   reorders or edits a section in the theme editor. Nothing here assumes a
   particular section exists — every lookup tolerates absence. */
(() => {
'use strict';
if (window.__wildearthBooted) return;
window.__wildearthBooted = true;

const rm   = matchMedia('(prefers-reduced-motion: reduce)').matches;
const fine = matchMedia('(hover:hover) and (pointer:fine)').matches;
const qs  = s => document.querySelector(s);
const qsa = s => [...document.querySelectorAll(s)];
const lerp  = (a,b,t) => a+(b-a)*t;
const clamp = (v,a,b) => Math.max(a,Math.min(b,v));

/* ---- runtime state, rebuilt by rescan() so reorder/add/remove just works ---- */
let layers = {};        // sceneKey -> { el, o }
let sceneEls = [];       // elements carrying [data-scene], in document order
let sequence = [];       // unique scene keys, in document order (the day arc)
let railWrap = null, railTime = null, railDots = [];
let plx = [];            // parallax targets (honours per-section toggle + reduced motion)
let pins = [];           // [{ pin, track, bar }]
let tickers = [];        // [{ track, x }]
let starsC = null, atmoC = null, motes = [];
let cur = '';            // active scene key
let HEADER_H = 0;        // theme sticky-header height, measured at runtime
const atmo = { ember:[229,129,41], mote:[201,212,205], star:[233,240,236] };  /* fallbacks; live values read from the CSS custom props */

/* ---- read a colour custom property into [r,g,b] (hex or rgb() forms) ---- */
function rgbOf(name, fallback){
  const el = qs('.wildearth'); if (!el) return fallback;
  let v = getComputedStyle(el).getPropertyValue(name).trim();
  if (!v) return fallback;
  if (v[0] === '#'){
    let h = v.slice(1);
    if (h.length === 3) h = [...h].map(c => c+c).join('');
    if (h.length >= 6) return [parseInt(h.slice(0,2),16),parseInt(h.slice(2,4),16),parseInt(h.slice(4,6),16)];
    return fallback;
  }
  const m = v.match(/[\d.]+/g);
  return (m && m.length >= 3) ? [+m[0],+m[1],+m[2]] : fallback;
}

/* ---- measure a sticky/fixed theme header, if any ---- */
function measureHeader(){
  let h = 0;
  document.querySelectorAll(
    '.shopify-section-group-header-group, #shopify-section-header, header.shopify-section,' +
    ' sticky-header, .header-wrapper, header[role="banner"], .site-header, #header'
  ).forEach(el => {
    const pos = getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') h = Math.max(h, el.getBoundingClientRect().height);
  });
  return h;
}

/* ---- (re)discover everything the engine touches ---- */
function rescan(){
  /* sky layers (each layer's 2nd class is its scene key) */
  layers = {};
  qsa('.wildearth .we-sky .layer').forEach(l => { const k = l.classList[1]; if (k) layers[k] = { el:l, o:(k===cur?1:0) }; });

  /* scenes present, in document order → the day arc + rail sequence */
  sceneEls = qsa('.wildearth [data-scene]').filter(el => el.dataset.scene);
  sequence = [];
  sceneEls.forEach(el => { const s = el.dataset.scene; if (!sequence.includes(s)) sequence.push(s); });

  /* rail: rebuild dots from the runtime sequence */
  railWrap = qs('.wildearth .we-rail');
  railTime = qs('.wildearth .we-railtime');
  railDots = [];
  if (railWrap){
    railWrap.querySelectorAll('a').forEach(a => a.remove());
    sequence.forEach(scene => {
      const a = document.createElement('a');
      a.dataset.s = scene;
      a.setAttribute('aria-label', scene);
      a.addEventListener('click', e => {
        e.preventDefault();
        const target = sceneEls.find(el => el.dataset.scene === scene);
        if (target) target.scrollIntoView({ behavior: rm ? 'auto' : 'smooth', block:'start' });
      });
      railWrap.appendChild(a);
      railDots.push(a);
    });
  }

  /* parallax targets — skip sections whose parallax toggle is off */
  plx = qsa('.wildearth:not(.we-no-parallax) [data-plx]').map(el => ({ el, f:+el.dataset.plx }));

  /* pinned notes scrollers (usually one, but tolerate any count) */
  pins = qsa('.wildearth .notes-pin').map(pin => ({
    pin, track: pin.querySelector('.we-notestrack'), bar: pin.querySelector('.notes-bar i')
  })).filter(p => p.track);

  /* marquees (skip any whose scroll toggle is off) */
  tickers = qsa('.wildearth:not(.we-no-marquee) .we-tickertrack').map(track => {
    const prev = tickers.find(t => t.track === track);
    return { track, x: prev ? prev.x : 0 };
  });

  /* canvases + atmosphere colours */
  starsC = qs('.wildearth .we-stars');
  atmoC  = qs('.wildearth .we-atmo');
  atmo.ember = rgbOf('--we-ember', atmo.ember);
  atmo.mote  = rgbOf('--we-mote',  atmo.mote);
  atmo.star  = rgbOf('--we-star',  atmo.star);

  HEADER_H = measureHeader();
  sizeCanvases();
  wireMobileNotes();
}

/* ---- active scene from scroll position ---- */
function activeScene(){
  if (!sceneEls.length) return null;
  const probe = innerHeight * .5;
  let s = sceneEls[0];
  for (const sec of sceneEls){ if (sec.getBoundingClientRect().top <= probe) s = sec; }
  return s;
}

/* ---- stars ---- */
function drawStars(){
  if (!starsC) return;
  const x = starsC.getContext('2d');
  starsC.width = innerWidth * devicePixelRatio; starsC.height = innerHeight * devicePixelRatio;
  x.scale(devicePixelRatio, devicePixelRatio);
  x.clearRect(0,0,innerWidth,innerHeight);
  const [r,g,b] = atmo.star;
  for (let i=0;i<140;i++){
    const px = Math.random()*innerWidth, py = Math.random()*innerHeight*.75;
    const rad = Math.random()*1.1+.2, a = Math.random()*.55+.15;
    x.fillStyle = `rgba(${r},${g},${b},${a})`;
    x.beginPath(); x.arc(px,py,rad,0,7); x.fill();
  }
}

/* ---- atmosphere particles ---- */
function setupAtmo(){
  if (!atmoC) return;
  atmoC.width = innerWidth * devicePixelRatio; atmoC.height = innerHeight * devicePixelRatio;
  const n = Math.min(70, innerWidth/18);
  motes = Array.from({length:n}, () => ({
    x: Math.random()*innerWidth, y: Math.random()*innerHeight,
    r: Math.random()*1.8+.5, s: Math.random()*.35+.08,
    dx: Math.random()*.3-.15, tw: Math.random()*6.28
  }));
}
function atmoFrame(t){
  if (!atmoC){ requestAnimationFrame(atmoFrame); return; }
  const x = atmoC.getContext('2d');
  x.setTransform(devicePixelRatio,0,0,devicePixelRatio,0,0);
  x.clearRect(0,0,innerWidth,innerHeight);
  const night = layers.night ? layers.night.o : 0;
  const dusk = (layers.dusk ? layers.dusk.o : 0) + (layers.dawn ? layers.dawn.o : 0);
  const [er,eg,eb] = atmo.ember, [mr,mg,mb] = atmo.mote;
  for (const m of motes){
    m.y -= m.s * (1 + night*1.6); m.x += m.dx + Math.sin(t/900 + m.tw)*.12;
    if (m.y < -6){ m.y = innerHeight + 6; m.x = Math.random()*innerWidth; }
    const twinkle = .5 + Math.sin(t/420 + m.tw)*.5;
    if (night > .04){
      x.fillStyle = `rgba(${er},${eg+Math.floor(60*twinkle)},${eb},${(0.16+.34*twinkle)*night})`;
    } else {
      x.fillStyle = `rgba(${mr},${mg+Math.floor(30*twinkle)},${mb},${(0.10+.16*twinkle)*(.5+dusk*.5)})`;
    }
    x.beginPath(); x.arc(m.x, m.y, m.r, 0, 7); x.fill();
  }
  requestAnimationFrame(atmoFrame);
}

/* ---- master loop ---- */
/* Live media queries — evaluated every frame, NOT latched once at load, so
   rotation / resize / a mis-read initial viewport can never leave the wrong
   engine running. The field notes unpin on touch OR narrow width (matching the
   CSS), because the pinned effect is a scroll-wheel metaphor a swipe can't drive. */
const narrowMQ    = matchMedia('(max-width:760px)');
const notesMQ     = matchMedia('(max-width:760px), (pointer:coarse)');
const touchDevice = matchMedia('(hover:none), (pointer:coarse)').matches;
const mobileNotes = () => notesMQ.matches;                     /* unpin the field notes? (live) */
const noParallax  = () => rm || touchDevice || narrowMQ.matches;
function frame(){
  const y = scrollY;
  HEADER_H = measureHeader();
  document.documentElement.style.setProperty('--we-header-offset', HEADER_H + 'px');

  /* scene + rail + body colour scheme */
  const sec = activeScene();
  if (sec){
    const scene = sec.dataset.scene;
    if (scene !== cur){
      cur = scene;
      document.body.dataset.scene = scene;
      railDots.forEach(d => d.classList.toggle('on', d.dataset.s === scene));
      if (railTime) railTime.textContent = sec.dataset.time || '';
    }
  }
  /* sky opacities — every present layer eases toward 1 if active else 0 */
  for (const k in layers){
    const L = layers[k];
    L.o = lerp(L.o, (k === cur) ? 1 : 0, .055);
    L.el.style.opacity = L.o.toFixed(3);
  }
  if (starsC) starsC.style.opacity = (layers.night ? layers.night.o : 0).toFixed(3);

  if (!noParallax()){
    for (const p of plx){
      const r = p.el.parentElement.getBoundingClientRect();
      const prog = clamp((innerHeight - r.top) / (innerHeight + r.height), 0, 1);
      p.el.style.transform = `translateY(${(prog - .5) * p.f}%)`;
    }
  }
  if (!rm){
    for (const t of tickers){
      t.x -= .6;
      const half = t.track.scrollWidth / 2;
      if (-t.x >= half) t.x += half;
      t.track.style.transform = `translateX(${t.x}px)`;
    }
    /* horizontal notes. On a touch / narrow surface the scroller is native —
       the pin loop MUST NOT touch the track (it would fight the swipe), and it
       clears any leftover transform so a mode-switch can never strand it. On a
       mouse desktop it runs the document-relative pin, header-offset included. */
    const swipe = mobileNotes();
    for (const p of pins){
      if (swipe){
        if (p.track.style.transform) p.track.style.transform = '';
        continue;
      }
      const pinStart = p.pin.getBoundingClientRect().top + y - HEADER_H;
      const total = p.pin.offsetHeight - innerHeight + HEADER_H;
      if (total <= 0) continue;
      const prog = clamp((y - pinStart) / total, 0, 1);
      const dist = p.track.scrollWidth - innerWidth + innerWidth * .08;
      p.track.style.transform = `translateX(${-prog * dist}px)`;
      if (p.bar) p.bar.style.transform = `scaleX(${prog})`;
    }
  }
  requestAnimationFrame(frame);
}

/* ---- reveals + values light-up (observers persist; new nodes get observed) ---- */
const io = new IntersectionObserver(es => es.forEach(e => {
  if (e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); }
}), { threshold:.16 });
const vio = new IntersectionObserver(es => es.forEach(e => e.target.classList.toggle('lit', e.isIntersecting)),
  { threshold:.6, rootMargin:'-18% 0px -18% 0px' });
function observe(scope){
  scope.querySelectorAll('.rv:not([data-we-obs]),.stag:not([data-we-obs])').forEach(el => { el.dataset.weObs='1'; io.observe(el); });
  scope.querySelectorAll('.vword:not([data-we-obs])').forEach(el => { el.dataset.weObs='1'; vio.observe(el); });
}

/* ---- per-scope enhancements (idempotent) ---- */
function enhance(scope){
  /* hero headline split into letters */
  const h1 = scope.querySelector('.we-heroline');
  if (h1 && !h1.querySelector('.w')){
    h1.innerHTML = h1.textContent.trim().split(' ').map(w =>
      `<span class="w">${[...w].map((c,i)=>`<span class="c" style="transition-delay:${.25+i*.035}s">${c}</span>`).join('')}</span>`
    ).join(' ');
  }
  /* live chip: the real SAST clock (the simulated viewer count was removed) */
  const sastEl = scope.querySelector('.we-sast');
  if (sastEl && !sastEl.dataset.weOn){
    sastEl.dataset.weOn = '1';
    const fmt = new Intl.DateTimeFormat('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Africa/Johannesburg'});
    const tick = () => sastEl.textContent = fmt.format(new Date());
    tick(); setInterval(tick, 20000);
  }
  /* if the form posted, scroll the confirmation into view */
  const posted = scope.querySelector('.success[data-posted]');
  if (posted) posted.scrollIntoView({ block:'center' });

  if (fine && !rm){
    /* magnetic buttons */
    scope.querySelectorAll('.mag:not([data-we-enh])').forEach(el => {
      el.dataset.weEnh = '1';
      el.addEventListener('mousemove', e => {
        const r = el.getBoundingClientRect();
        const dx = e.clientX-(r.left+r.width/2), dy = e.clientY-(r.top+r.height/2);
        el.style.transform = `translate(${clamp(dx*.18,-10,10)}px,${clamp(dy*.3,-8,8)}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform=''; el.style.transition='transform .35s cubic-bezier(.2,.7,.2,1)';
        setTimeout(()=>el.style.transition='',380); });
    });
    /* card glow + tilt */
    scope.querySelectorAll('.card.tilt:not([data-we-enh])').forEach(card => {
      card.dataset.weEnh = '1';
      card.addEventListener('mousemove', e => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX-r.left)/r.width, py = (e.clientY-r.top)/r.height;
        card.style.setProperty('--mx', (px*100)+'%'); card.style.setProperty('--my', (py*100)+'%');
        card.style.transform = `perspective(900px) rotateX(${(py-.5)*-4}deg) rotateY(${(px-.5)*5}deg)`;
      });
      card.addEventListener('mouseleave', () => card.style.transform='');
    });
  }
}

/* ---- loader → ready ---- */
function ready(){ document.documentElement.classList.add('we-ready'); }

/* ---- sizing ---- */
function sizeCanvases(){ drawStars(); if (!mobileNotes()) setupAtmo(); }   /* no particle field on mobile */

/* ---- mobile: drive the (re-shown) progress bar from native horizontal scroll ---- */
function wireMobileNotes(){
  if (!mobileNotes()) return;
  qsa('.wildearth .notes-pin').forEach(pin => {
    const sc = pin.querySelector('.notes-scroller'), fill = pin.querySelector('.notes-bar i');
    if (!sc || !fill || sc.dataset.weWired) return;
    sc.dataset.weWired = '1';
    const upd = () => { const max = sc.scrollWidth - sc.clientWidth;
      fill.style.transform = `scaleX(${max > 0 ? sc.scrollLeft / max : 0})`; };
    sc.addEventListener('scroll', upd, { passive:true });
    upd();
  });
}

/* ---- boot ---- */
function boot(){
  rescan();
  observe(document);
  enhance(document);
  if (rm || window.Shopify?.designMode) ready();
  else {
    let done = false;
    const go = () => { if (!done){ done = true; ready(); } };
    if (document.readyState === 'complete') setTimeout(go, 1200);
    else window.addEventListener('load', () => setTimeout(go, 1500));
    setTimeout(go, 3400);
  }
  addEventListener('resize', onViewportChange);
  addEventListener('orientationchange', onViewportChange);
  /* react the instant the breakpoint / pointer type flips (rotation, split-view) */
  const onMQ = () => onViewportChange();
  (notesMQ.addEventListener ? notesMQ.addEventListener('change', onMQ) : notesMQ.addListener(onMQ));
  (narrowMQ.addEventListener ? narrowMQ.addEventListener('change', onMQ) : narrowMQ.addListener(onMQ));
  requestAnimationFrame(frame);
  if (!rm && !mobileNotes()) requestAnimationFrame(atmoFrame);   /* particle loop off on mobile */
}
/* re-measure, re-size canvases, and (re)wire the mobile swipe progress bar. The
   pin loop itself already reads the breakpoint live, so it self-corrects. */
function onViewportChange(){ HEADER_H = measureHeader(); sizeCanvases(); wireMobileNotes(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

/* ---- theme editor: a section was added / edited / selected ---- */
function refresh(e){
  const scope = (e && e.target && e.target.querySelector) ? e.target : document;
  rescan();
  observe(scope === document ? document : document);
  enhance(scope);
}
document.addEventListener('shopify:section:load', refresh);
document.addEventListener('shopify:section:select', refresh);
document.addEventListener('shopify:section:unload', () => rescan());
})();
